import { IeltsTheme } from '@/types';

/**
 * Single source of truth for every colour that varies by IELTS theme.
 *
 * Consumers should never compare against IeltsTheme to pick a colour. Read a
 * token instead — either through the generated CSS variables (`var(--ielts-fg)`,
 * or the Tailwind aliases `text-ielts-fg` / `bg-ielts-surface` / `border-ielts-line`)
 * or through `useIeltsTheme()` when the value has to reach JS.
 *
 * The neutral scale replaces ~200 ad-hoc greys that were previously written
 * inline at each call site. The accent group exists because the "primary
 * background + white text" recipe only works for themes whose colorPrimary is
 * dark enough; see ACCESSIBILITY notes below.
 */
export type IeltsThemeObject = {
  // --- existing, component-specific tokens (unchanged) ---
  colorPrimary: string;
  inputColorBorderButton?: string;
  inputBorderColor: string;
  hoverOptionBgColor: string;
  questionIndexBgColor: string;
  questionIndexShadowColor: string;
  recordRingColor: string;
  recordCircleColor: string;
  waveColor?: string;
  progressBarColor: string;

  // --- neutral scale ---
  /**
   * Whether this theme paints on a dark ground. Carried by the neutral set, so
   * every theme gets it from whichever of `lightNeutral` / `darkNeutral` it
   * spreads — there is no list of dark themes to keep in sync.
   *
   * Read it only where a boolean is unavoidable (antd's `darkAlgorithm`). For
   * colours, read the token; never branch on this to pick one.
   */
  isDark: boolean;
  /** Page background. */
  bg: string;
  /** Card / modal / panel sitting on top of `bg`. */
  surface: string;
  /** Inset or secondary panel, chips, skeleton blocks. */
  surfaceMuted: string;
  /** Default hairline border. */
  line: string;
  /** Emphasised border — inputs, selected states. */
  lineStrong: string;
  /** Primary body text. */
  fg: string;
  /** Secondary text — labels, captions still meant to be read. */
  fgSecondary: string;
  /** De-emphasised text — placeholders, disabled, metadata. */
  fgMuted: string;
  /**
   * Interactive control ground — inputs, dropdowns, option cards, raised
   * buttons, and the classic exam chrome.
   *
   * Equal to `surface` in all nine light themes, so this is only ever a distinct
   * step in CLASSIC_BLACK, where a control has to lift off the near-black
   * ground. Keep reading it rather than `surface` at control call sites: it is
   * the one place a theme can re-tint every control at once, which is exactly
   * how CLASSIC_LIGHT_ORANGE's #EEDCBA wash was switched off.
   */
  control: string;
  /** Pulsing block in loading skeletons. */
  skeleton: string;
  /**
   * Corner radius of text inputs and select triggers, as a CSS length.
   *
   * The only non-colour token here. CLASSIC_BLACK and CLASSIC_LIGHT_ORANGE draw
   * squared controls while the other eight draw pills, and that was the last
   * reason left to branch on the theme inside an input component.
   */
  controlRadius: string;

  // --- accent group ---
  /** `colorPrimary` when used as a *foreground* (text, icon, slider). */
  accent: string;
  /** Filled accent background, e.g. an answered question chip. */
  accentBg: string;
  /** Text/icon colour that sits on top of `accentBg`. */
  onAccent: string;
  /** Soft accent wash — active/hover background. */
  tint: string;
  /** Border that pairs with `tint`. */
  tintLine: string;
  /**
   * Text on a tinted surface in its idle (not answered) state. Always a real
   * colour, never `inherit`: its only consumer writes it as an inline `color`,
   * which would outrank the element's own text class.
   */
  fgIdle: string;

  // --- status ---
  /** Correct-answer fill. */
  successBg: string;
  /** Correct-answer text. */
  successFg: string;
  /** Correct-answer border. Theme-invariant: it has to read on either ground. */
  successLine: string;
  /** Wrong-answer fill. */
  errorBg: string;
  /** Wrong-answer text. */
  errorFg: string;
  /** Wrong-answer border. Theme-invariant. */
  errorLine: string;
  /** Informational / link text. */
  infoFg: string;
};

const lightStatus = {
  successBg: '#E8F5E9',
  successFg: '#337C4A',
  successLine: '#30B639',
  errorBg: '#FFEBEE',
  errorFg: '#C21111',
  errorLine: '#C21111',
  infoFg: '#2563EB',
} as const;

const darkStatus = {
  successBg: '#2E5C3A',
  // Call sites used #8AD09C / #FF6B6B, which land at 4.28:1 and 4.01:1 on their
  // own fills. Lifted just enough to clear AA.
  successFg: '#8ED6A1',
  successLine: '#30B639',
  errorBg: '#5C2E2E',
  errorFg: '#FF7E7E',
  errorLine: '#C21111',
  infoFg: '#60A5FA',
} as const;

const lightNeutral = {
  isDark: false,
  bg: '#FCFCFC',
  surface: '#FFFFFF',
  surfaceMuted: '#F5F5F5',
  line: '#EDEDED',
  lineStrong: '#E0E0E0',
  fg: '#0F0F0F',
  fgSecondary: '#515159',
  // Nudged down from the gray-500 #6B7280 that most call sites used: that only
  // reached 4.43:1 on `surfaceMuted`, just under AA. Same hue, 4.57:1.
  fgMuted: '#69707D',
  control: '#FFFFFF',
  skeleton: '#E5E7EB',
  controlRadius: '100px',
} as const;

const darkNeutral = {
  isDark: true,
  // Near-black grounds (#090909 / #17181A) made the card all but vanish into the
  // page and pushed white body text to 21:1, which is fatiguing over a full
  // reading passage. Lifted to a neutral dark grey — same cool hue, and the
  // bg → surface → control ramp now steps evenly instead of jumping at the end.
  bg: '#16171A',
  surface: '#202227',
  surfaceMuted: '#2E3036',
  line: '#3D3D3D',
  lineStrong: '#474747',
  fg: '#FFFFFF',
  fgSecondary: '#D1D5DB',
  fgMuted: '#9CA3AF',
  control: '#2E3036',
  skeleton: '#2E3036',
  controlRadius: '4px',
} as const;

/**
 * A theme entry as it is written out: every token except the accent group, which
 * the two recipes below derive from it.
 */
type ThemeBase = Omit<
  IeltsThemeObject,
  'accent' | 'accentBg' | 'onAccent' | 'tint' | 'tintLine' | 'fgIdle'
>;

/**
 * The historical recipe: fill with `colorPrimary`, write on it in white, wash
 * with the theme's soft tint.
 *
 * Both colours are read off `base` — the fill from `colorPrimary`, the wash from
 * `hoverOptionBgColor`, which is the same value in all ten themes — rather than
 * passed in beside it. They used to be arguments, which meant every theme spelt
 * its brand hex twice: editing `colorPrimary` alone left the accent group, and
 * so every antd component seeded from it, on the old colour.
 *
 * Only applied to themes whose colorPrimary is dark enough to carry white text:
 * TEAL_BLUE (4.57:1), STEEL_BLUE (4.89:1) and the two black themes.
 *
 * STILL OUTSTANDING: GAMIFICATION (4.32:1) and BRIGHT_BLUE (3.28:1) are below
 * AA for body-size text but above the 3:1 UI-component threshold, so they were
 * left as-is pending design review. Accessible replacements if wanted:
 * GAMIFICATION accentBg #CCE2F0 / accent #0D659C (4.68:1),
 * BRIGHT_BLUE accentBg #CCE1F0 / accent #0D65A0 (4.61:1).
 *
 * YELLOW_ORANGE, GREEN and DUSTY_PINK failed badly (1.61–1.86:1) and now spell
 * their accents out. LIGHT_ORANGE already shipped that treatment by hand.
 */
const whiteOnPrimary = (base: ThemeBase): IeltsThemeObject => ({
  ...base,
  accent: base.colorPrimary,
  accentBg: base.colorPrimary,
  onAccent: '#FFFFFF',
  tint: base.hoverOptionBgColor,
  tintLine: base.colorPrimary,
  fgIdle: lightNeutral.fgSecondary,
});

/**
 * The accent group written out, for the five themes that cannot derive it.
 *
 * Four of them are the treatment CLASSIC_LIGHT_ORANGE already shipped: when
 * `colorPrimary` is too light to carry white text, fill with a tint of the same
 * hue and write on it in a dark shade of that hue. `colorPrimary` itself is
 * deliberately left alone — it stays the brand colour and still feeds antd's
 * ConfigProvider. The fifth is the dark theme, whose primary is darker than its
 * own page ground.
 *
 * `onAccent` follows `accent` and `tint` still comes from `hoverOptionBgColor`,
 * so those two are not repeated at the call site either.
 */
const withAccents = (
  base: ThemeBase,
  accents: { accent: string; accentBg: string; tintLine: string },
): IeltsThemeObject => ({
  ...base,
  ...accents,
  onAccent: accents.accent,
  tint: base.hoverOptionBgColor,
  fgIdle: lightNeutral.fgSecondary,
});

export const themeData: Record<IeltsTheme, IeltsThemeObject> = {
  // proposed accessible accent: accentBg #CCE2F0 / onAccent #0D659C (4.68:1)
  [IeltsTheme.GAMIFICATION]: whiteOnPrimary({
    colorPrimary: '#017ECC',
    inputBorderColor: '#75B9FC',
    hoverOptionBgColor: '#E6F4FF',
    questionIndexBgColor: '#FFE70B',
    questionIndexShadowColor: '#B3A218',
    recordCircleColor: '#117BFF',
    recordRingColor: '#3AECF4',
    progressBarColor: '#FFE70B',
    ...lightNeutral,
    ...lightStatus,
  }),
  [IeltsTheme.CLASSIC_BLACK]: {
    ...withAccents(
      {
        colorPrimary: '#0F0F0F',
        inputColorBorderButton: '#D2D8DF',
        inputBorderColor: '#0F0F0F',
        // was '#F5F5F5' — a light value inside the dark theme. Never surfaced
        // because every call site hardcoded '#2E3036' instead of reading it.
        hoverOptionBgColor: '#2E3036',
        questionIndexBgColor: '#EBEBEB',
        questionIndexShadowColor: '#B3B3B3',
        recordCircleColor: '#ABAAA6',
        recordRingColor: '#E0E0E0',
        progressBarColor: '#ABAAA6',
        ...darkNeutral,
        ...darkStatus,
      },
      // The primary is #0F0F0F, darker than this theme's own ground, so the fill
      // steps up to the raised grey and the accent writes in white instead.
      { accent: '#FFFFFF', accentBg: '#2E3036', tintLine: '#2E3036' },
    ),
    // `withAccents` defaults this to the light ramp's secondary text, which is
    // the wrong ground here.
    fgIdle: '#8C8C8C',
  },
  [IeltsTheme.CLASSIC_TEAL_BLUE]: {
    ...whiteOnPrimary({
      colorPrimary: '#10809D',
      inputColorBorderButton: '#DEE1E7',
      inputBorderColor: '#10809D',
      hoverOptionBgColor: '#E9FAFF',
      questionIndexBgColor: '#EBEBEB',
      questionIndexShadowColor: '#B3B3B3',
      recordCircleColor: '#FD7307',
      recordRingColor: '#E9FAFF',
      waveColor: '#FD7307',
      progressBarColor: '#10809D',
      ...lightNeutral,
      ...lightStatus,
    }),
    // colorPrimary carries white fine (4.57:1), but as a *foreground* on its own
    // tint it only reached 4.26:1. 4% darker clears AA at 4.55:1; the fill and
    // the brand colour stay #10809D.
    accent: '#0F7B97',
  },
  [IeltsTheme.CLASSIC_STEEL_BLUE]: whiteOnPrimary({
    colorPrimary: '#3F72B6',
    inputColorBorderButton: '#DEE1E7',
    inputBorderColor: '#3F72B6',
    hoverOptionBgColor: '#E9FAFF',
    questionIndexBgColor: '#EBEBEB',
    questionIndexShadowColor: '#B3B3B3',
    recordCircleColor: '#DA3324',
    recordRingColor: '#E9FAFF',
    waveColor: '#DA3324',
    progressBarColor: '#3F72B6',
    ...lightNeutral,
    ...lightStatus,
  }),
  [IeltsTheme.CLASSIC_YELLOW_ORANGE]: withAccents(
    {
      colorPrimary: '#FFAE00',
      inputColorBorderButton: '#DEE1E7',
      inputBorderColor: '#FFAE00',
      hoverOptionBgColor: '#FFF9E9',
      questionIndexBgColor: '#EBEBEB',
      questionIndexShadowColor: '#B3B3B3',
      recordCircleColor: '#D09C3B',
      recordRingColor: '#FFF9E9',
      waveColor: '#FFAE00',
      progressBarColor: '#FFAE00',
      ...lightNeutral,
      ...lightStatus,
    },
    // White on #FFAE00 was 1.86:1. Now #845E0B on #F0E4CC = 4.65:1.
    { accent: '#845E0B', accentBg: '#F0E4CC', tintLine: '#DDCCA6' },
  ),
  // Marginal at 3.28:1. Proposed: accentBg #CCE1F0 / onAccent #0D65A0 (4.61:1)
  [IeltsTheme.CLASSIC_BRIGHT_BLUE]: whiteOnPrimary({
    colorPrimary: '#3895D3',
    inputColorBorderButton: '#DEE1E7',
    inputBorderColor: '#3895D3',
    hoverOptionBgColor: '#E9FAFF',
    questionIndexBgColor: '#EBEBEB',
    questionIndexShadowColor: '#B3B3B3',
    recordCircleColor: '#EBCC91',
    recordRingColor: '#E9FAFF',
    waveColor: '#EBCC91',
    progressBarColor: '#3895D3',
    ...lightNeutral,
    ...lightStatus,
  }),
  [IeltsTheme.CLASSIC_GREEN]: withAccents(
    {
      colorPrimary: '#C4D29F',
      inputColorBorderButton: '#DEE1E7',
      inputBorderColor: '#7C824F',
      hoverOptionBgColor: '#FCFFE9',
      questionIndexBgColor: '#EBEBEB',
      questionIndexShadowColor: '#B3B3B3',
      recordCircleColor: '#EBCC91',
      recordRingColor: '#FCFFE9',
      waveColor: '#C4D29F',
      progressBarColor: '#C4D29F',
      ...lightNeutral,
      ...lightStatus,
    },
    // White on #C4D29F was 1.61:1, the worst of the ten.
    // Now #5B6F25 on #E3EAD2 = 4.53:1.
    { accent: '#5B6F25', accentBg: '#E3EAD2', tintLine: '#CCD8AC' },
  ),
  [IeltsTheme.CLASSIC_LIGHT_ORANGE]: {
    ...withAccents(
      {
        colorPrimary: '#FB9E61',
        inputColorBorderButton: '#DEE1E7',
        // Every input call site overrode the old '#FB9E61' with '#DFCBA5' — this
        // theme's inputs are washed, not saturated. Recorded here instead.
        inputBorderColor: '#DFCBA5',
        hoverOptionBgColor: '#FFF9E9',
        questionIndexBgColor: '#EBEBEB',
        questionIndexShadowColor: '#B3B3B3',
        recordCircleColor: '#DF7027',
        recordRingColor: '#FFF9E9',
        waveColor: '#FB9E61',
        progressBarColor: '#FB9E61',
        ...lightNeutral,
        ...lightStatus,
        // This theme used to override `control` with a #EEDCBA wash. Because that
        // one token grounds every control, the wash reached inputs, selects,
        // popover panels, the A/B/C option circles, the exam page ground and the
        // classic header/footer alike, and the whole app came out peach. It takes
        // the shared white from `lightNeutral` now, like the other eight light
        // themes; the theme still reads as itself through `colorPrimary`, the
        // accent group and the #DFCBA5 input border.
        //
        // `fgMuted` was darkened to #5B616D here for the same reason — the muted
        // grey only reached 3.70:1 on that wash. Back on the shared value too.

        // The one light theme that squares its inputs rather than rounding them.
        controlRadius: '4px',
      },
      // The one theme that already ships an accessible accent (6.34:1). These
      // values were previously hardcoded across 43 `isOrangeTheme` branches.
      { accent: '#9B1F11', accentBg: '#F1E2C9', tintLine: '#DFCBA5' },
    ),
    // Came from those same branches, a shade off the shared secondary text.
    fgIdle: '#505460',
  },
  [IeltsTheme.CLASSIC_DUSTY_PINK]: withAccents(
    {
      colorPrimary: '#EAB6B6',
      inputColorBorderButton: '#DEE1E7',
      inputBorderColor: '#EAB6B6',
      hoverOptionBgColor: '#FFF2E9',
      questionIndexBgColor: '#EBEBEB',
      questionIndexShadowColor: '#B3B3B3',
      recordCircleColor: '#EB7777',
      recordRingColor: '#FFF2E9',
      waveColor: '#EAB6B6',
      progressBarColor: '#EAB6B6',
      ...lightNeutral,
      ...lightStatus,
    },
    // White on #EAB6B6 was 1.77:1. Now #B51717 on #F0CCCC = 4.59:1.
    { accent: '#B51717', accentBg: '#F0CCCC', tintLine: '#DDA6A6' },
  ),
  [IeltsTheme.CLASSIC_BLACK_LIGHT]: whiteOnPrimary({
    colorPrimary: '#0F0F0F',
    inputColorBorderButton: '#D2D8DF',
    inputBorderColor: '#0F0F0F',
    hoverOptionBgColor: '#F5F5F5',
    questionIndexBgColor: '#EBEBEB',
    questionIndexShadowColor: '#B3B3B3',
    recordCircleColor: '#ABAAA6',
    recordRingColor: '#E0E0E0',
    progressBarColor: '#0F0F0F',
    ...lightNeutral,
    ...lightStatus,
  }),
};

/** One channel of `luminance`, gamma-expanded. */
const channelLuminance = (value: number) => {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

/** WCAG relative luminance of a 3- or 6-digit hex colour. */
const luminance = (hex: string) => {
  const value = hex.replace('#', '');
  const full = value.length === 3 ? value.replace(/./g, (c) => c + c) : value;
  const [r = 0, g = 0, b = 0] = [0, 2, 4].map((i) =>
    channelLuminance(parseInt(full.slice(i, i + 2), 16)),
  );
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** WCAG contrast ratio between two hex colours, 1 … 21. */
const contrastRatio = (a: string, b: string) => {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

/**
 * Text colour for a surface filled with an arbitrary theme colour.
 *
 * The accent group above hand-picks "text on fill" per theme, which only works
 * for the fills it knows about. A few antd components are painted straight from
 * `colorPrimary` instead, and that value swings from #0F0F0F to #C4D29F across
 * the ten themes — no single literal is readable on all of them.
 *
 * White stays while it clears 3:1, the WCAG floor for the bold labels these
 * fills carry, so the five dark primaries keep the white-on-primary look they
 * ship today. On the four pale primaries white drops to 1.6–2.1:1 and the
 * neutral ink takes over at 9:1 or better.
 */
export const readableOn = (background: string) =>
  contrastRatio('#FFFFFF', background) >= 3 ? '#FFFFFF' : lightNeutral.fg;

const kebab = (key: string) => key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

/**
 * Emit every theme as a `[data-ielts-theme="..."]` custom-property block.
 *
 * Rendered once into the document head so the correct palette is present in the
 * SSR payload — the pre-hydration script in `_document` already stamps
 * `data-ielts-theme` on `<html>`, so there is no flash and no client work.
 */
export function themeCssVars(): string {
  return Object.entries(themeData)
    .map(([name, tokens]) => {
      const decls = Object.entries(tokens)
        .filter(([, value]) => typeof value === 'string')
        .map(([key, value]) => `--ielts-${kebab(key)}:${value}`)
        .join(';');
      return `[data-ielts-theme="${name}"]{${decls}}`;
    })
    .join('');
}
